import type { CapacityNode } from "../../api/types";
import { describeGpu } from "../../lib/capacity";

interface GpuPillsProps {
  node: CapacityNode;
}

/**
 * The GPUs of one machine, one pill each.
 *
 * Colour alone never carries the meaning: each state also has its own fill
 * pattern (solid, striped, outlined, crossed) and every pill carries a full
 * sentence as its accessible name and tooltip.
 *
 * Args:
 *     node: The machine as measured.
 *
 * Returns:
 *     A list of GPU pills.
 */
export function GpuPills({ node }: GpuPillsProps): JSX.Element {
  if (node.gpus.length === 0) {
    return (
      <ul className="gpu-pills" aria-label={`GPUs of ${node.name}`}>
        {Array.from({ length: Math.max(0, node.gpu_total) }, (_, index) => (
          <li
            key={index}
            className="gpu-pill"
            data-state="unknown"
            title={`GPU ${index} — this machine is still starting`}
          >
            <span aria-hidden="true">{index}</span>
            <span className="sr-only">
              GPU {index} — this machine is still starting
            </span>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul className="gpu-pills" aria-label={`GPUs of ${node.name}`}>
      {node.gpus.map((gpu) => {
        const description = describeGpu(gpu, node.gpus_measured);
        return (
          <li
            key={gpu.index}
            className="gpu-pill"
            data-state={gpu.state}
            data-measured={node.gpus_measured}
            title={description}
          >
            {/* The number is only shown when it is a device number. Otherwise
                it is the position of a square, and printing it would send a
                researcher looking for "GPU 2" in nvidia-smi. */}
            <span aria-hidden="true">{node.gpus_measured ? gpu.index : "·"}</span>
            <span className="sr-only">{description}</span>
          </li>
        );
      })}
    </ul>
  );
}

const LEGEND: [string, string][] = [
  ["busy", "computing"],
  ["held", "in use"],
  ["idle", "held but idle"],
  ["free", "free"],
  ["unschedulable", "unavailable"],
];

/** Legend explaining the pill colours and patterns. */
export function GpuLegend(): JSX.Element {
  return (
    <ul className="gpu-legend">
      {LEGEND.map(([state, label]) => (
        <li key={state} className="gpu-legend__item">
          <span className="gpu-legend__swatch" data-state={state} aria-hidden="true" />
          {label}
        </li>
      ))}
    </ul>
  );
}
